import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog } from '@headlessui/react';
import {
  DocumentChartBarIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { boardReportApi } from '../lib/api';
import FeatureGate from '../components/FeatureGate';
import PermissionGate from '../components/PermissionGate';
import toast from 'react-hot-toast';
import { PageHeader, StatusBadge, LoadingSpinner, EmptyState } from '../components/ui';
import ModuleConnections from '../components/help/ModuleConnections';
import { useTeamId } from '../hooks/useTeamId';

const PERIOD_OPTIONS = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'ANNUAL', label: 'Annual' },
  { value: 'CUSTOM', label: 'Custom' },
] as const;

function getDefaultDates(period: string) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  switch (period) {
    case 'MONTHLY': {
      const start = new Date(year, month - 1, 1);
      const end = new Date(year, month, 0);
      return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
    }
    case 'QUARTERLY': {
      const qStart = Math.floor((month - 1) / 3) * 3;
      const start = new Date(year, qStart, 1);
      const end = new Date(year, qStart + 3, 0);
      return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
    }
    case 'ANNUAL': {
      return { start: `${year - 1}-01-01`, end: `${year - 1}-12-31` };
    }
    default:
      return { start: '', end: '' };
  }
}

export default function BoardReportsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [period, setPeriod] = useState<string>('MONTHLY');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [title, setTitle] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Get user's team
  const { teamId } = useTeamId();

  // Fetch board reports
  const { data: reportsData, isLoading } = useQuery({
    queryKey: ['board-reports', teamId],
    queryFn: async () => {
      const res = await boardReportApi.list(teamId!, { limit: 50 });
      return res.data;
    },
    enabled: !!teamId,
    // Poll for GENERATING status
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.reports?.some((r: any) => r.status === 'GENERATING')) return 5000;
      return false;
    },
  });

  // Generate mutation
  const generateMutation = useMutation({
    mutationFn: async () => {
      await boardReportApi.generate(teamId!, {
        period: period as any,
        periodStart,
        periodEnd,
        title: title || undefined,
      });
    },
    onSuccess: () => {
      toast.success('Board report generation started');
      queryClient.invalidateQueries({ queryKey: ['board-reports'] });
      setShowGenerateModal(false);
      resetForm();
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to generate report'),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (reportId: string) => {
      await boardReportApi.delete(teamId!, reportId);
    },
    onSuccess: () => {
      toast.success('Report deleted');
      queryClient.invalidateQueries({ queryKey: ['board-reports'] });
      setDeleteConfirm(null);
    },
    onError: () => toast.error('Failed to delete report'),
  });

  // Download PDF
  const handleDownload = async (reportId: string, reportTitle: string) => {
    try {
      const res = await boardReportApi.downloadPdf(teamId!, reportId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportTitle.replace(/\s+/g, '-').toLowerCase()}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const resetForm = () => {
    setPeriod('MONTHLY');
    setPeriodStart('');
    setPeriodEnd('');
    setTitle('');
  };

  const openGenerateModal = () => {
    const defaults = getDefaultDates('MONTHLY');
    setPeriodStart(defaults.start);
    setPeriodEnd(defaults.end);
    setShowGenerateModal(true);
  };

  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod);
    if (newPeriod !== 'CUSTOM') {
      const defaults = getDefaultDates(newPeriod);
      setPeriodStart(defaults.start);
      setPeriodEnd(defaults.end);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const statusBadge = (status: string, totalScans?: number) => {
    switch (status) {
      case 'GENERATING':
        return (
          <StatusBadge variant="warning">
            <svg className="animate-spin -ml-0.5 mr-1.5 h-3 w-3" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Generating
          </StatusBadge>
        );
      case 'READY':
        if (!totalScans || totalScans === 0) {
          return <StatusBadge variant="warning">No Activity</StatusBadge>;
        }
        return <StatusBadge variant="success">Ready</StatusBadge>;
      case 'ERROR':
        return <StatusBadge variant="danger">Error</StatusBadge>;
      default:
        return <StatusBadge variant="neutral">{status}</StatusBadge>;
    }
  };

  return (
    <FeatureGate feature="board_reports" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          title="Board Reports"
          description="Generate compliance reports for board of directors and committee presentations"
          actions={
            <PermissionGate teamId={teamId} permission="compliance.write">
              <button
                onClick={openGenerateModal}
                disabled={!teamId}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-ocean-600 rounded-md hover:bg-ocean-700 disabled:opacity-50"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Generate Report
              </button>
            </PermissionGate>
          }
        />

        <ModuleConnections moduleKey="board-reports" />

        {/* Reports table */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          {isLoading ? (
            <LoadingSpinner size="md" className="py-12" />
          ) : !reportsData?.reports?.length ? (
            <EmptyState
              icon={DocumentChartBarIcon}
              title="No board reports yet"
              description="Generate your first report for your board of directors."
              action={
                <button
                  onClick={openGenerateModal}
                  disabled={!teamId}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-ocean-600 hover:text-ocean-800"
                >
                  Generate Report
                </button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Range</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scans</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportsData.reports.map((report: any) => (
                    <tr key={report.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/board-reports/${report.id}`)}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-navy-900">
                        {report.title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {report.period}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(report.periodStart)} - {formatDate(report.periodEnd)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {statusBadge(report.status, report.totalScans)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {report.status === 'READY' ? (
                          <span>
                            {report.totalScans} total / <span className="text-green-600">{report.passedScans} passed</span> / <span className="text-red-600">{report.failedScans} failed</span>
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(report.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          {report.status === 'READY' && (
                            <button
                              onClick={() => handleDownload(report.id, report.title)}
                              className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-ocean-700 bg-ocean-50 rounded hover:bg-ocean-100"
                            >
                              <ArrowDownTrayIcon className="w-3.5 h-3.5 mr-1" />
                              PDF
                            </button>
                          )}
                          <button
                            onClick={() => setDeleteConfirm(report.id)}
                            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100"
                          >
                            <TrashIcon className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Generate Modal */}
        <Dialog open={showGenerateModal} onClose={() => setShowGenerateModal(false)} className="relative z-50">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
              <Dialog.Title className="text-lg font-semibold text-navy-900">Generate Board Report</Dialog.Title>
              <p className="mt-1 text-sm text-gray-500">
                Auto-populate compliance data for the selected period.
              </p>

              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
                  <select
                    value={period}
                    onChange={(e) => handlePeriodChange(e.target.value)}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
                  >
                    {PERIOD_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={periodStart}
                      onChange={(e) => setPeriodStart(e.target.value)}
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      value={periodEnd}
                      onChange={(e) => setPeriodEnd(e.target.value)}
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title (optional)</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Auto-generated if empty"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => { setShowGenerateModal(false); resetForm(); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={() => generateMutation.mutate()}
                  disabled={!periodStart || !periodEnd || generateMutation.isPending}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-ocean-600 rounded-md hover:bg-ocean-700 disabled:opacity-50"
                >
                  {generateMutation.isPending ? 'Generating...' : 'Generate Report'}
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} className="relative z-50">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
              <Dialog.Title className="text-lg font-semibold text-navy-900">Delete Report</Dialog.Title>
              <p className="mt-2 text-sm text-gray-500">
                Are you sure you want to delete this board report? This action cannot be undone.
              </p>
              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteConfirm && deleteMutation.mutate(deleteConfirm)}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </div>
    </FeatureGate>
  );
}
